import React, { useEffect, useRef, useState } from 'react';
import { AdminGrantError, AdminGrantItemType, AdminGrantOptions, AdminGrantType, gameApi } from '../api';

const itemTypes: AdminGrantItemType[] = ['CommonItem', 'UniqueItem', 'Spell', 'Skill', 'Ally', 'AllyFromBox', 'Monster', 'Exhibit', 'GateTrophy', 'Corruption'];
const labels: Record<AdminGrantType, string> = {
  CommonItem: 'Common Items', UniqueItem: 'Unique Items', Spell: 'Spells', Skill: 'Skills', Ally: 'Allies',
  AllyFromBox: 'Allies from the box',
  Monster: 'Monster Trophies', Exhibit: 'Exhibits', GateTrophy: 'Gate Trophies', Corruption: 'Corruptions',
  Money: 'Money', Clues: 'Clues',
};

interface Props {
  gameId: string;
  defaultInvestigator?: string;
  refreshVersion: number;
  onRefreshGameState?: () => void | Promise<void>;
  onCheckpointChanged?: () => void;
}

export const AdminGrantPanel: React.FC<Props> = ({ gameId, defaultInvestigator, refreshVersion, onRefreshGameState, onCheckpointChanged }) => {
  const [options, setOptions] = useState<AdminGrantOptions | null>(null);
  const [type, setType] = useState<AdminGrantType>('CommonItem');
  const [investigator, setInvestigator] = useState('');
  const [deckIndex, setDeckIndex] = useState('');
  const [amount, setAmount] = useState('1');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const currentGame = useRef(gameId);
  const preferredInvestigator = useRef(defaultInvestigator);
  const mounted = useRef(false);
  currentGame.current = gameId;
  preferredInvestigator.current = defaultInvestigator;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    setOptions(null);
    setType('CommonItem');
    setInvestigator('');
    setDeckIndex('');
    setAmount('1');
    setError(null);
    setSuccess(null);
    setSubmitting(false);
  }, [gameId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setOptions(null);
    gameApi.getAdminGrantOptions(gameId).then(data => {
      if (cancelled || currentGame.current !== gameId) return;
      setOptions(data);
      setInvestigator(previous => data.investigators.includes(previous) ? previous
        : data.investigators.includes(preferredInvestigator.current ?? '') ? preferredInvestigator.current!
        : data.investigators[0] ?? '');
    }).catch(err => {
      if (!cancelled && currentGame.current === gameId) {
        setError(err instanceof Error ? err.message : 'Failed to load grant options');
      }
    }).finally(() => {
      if (!cancelled && currentGame.current === gameId) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [gameId, refreshVersion, reload]);

  const isResource = type === 'Money' || type === 'Clues';
  const entries = !isResource && options ? options.items[type as AdminGrantItemType] : [];
  useEffect(() => {
    setDeckIndex(previous => entries.some(entry => String(entry.deckIndex) === previous)
      ? previous : entries.length ? String(entries[0].deckIndex) : '');
  }, [options, type]); // eslint-disable-line react-hooks/exhaustive-deps
  const numericAmount = Number(amount);
  const validAmount = amount.trim() !== '' && Number.isInteger(numericAmount) && numericAmount > 0 && numericAmount <= 2147483647;
  const canGrant = options?.gameId === gameId && options.investigators.includes(investigator)
    && (isResource ? validAmount : entries.some(entry => String(entry.deckIndex) === deckIndex));

  const grant = async () => {
    if (!canGrant || !options || loading || submitting) return;
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await gameApi.adminGrant(gameId, {
        investigatorName: investigator, expectedStateRevision: options.stateRevision, type,
        ...(isResource ? { amount: numericAmount } : { deckIndex: Number(deckIndex) }),
      });
      if (!mounted.current || currentGame.current !== gameId) return;
      setSuccess(result.message);
      onCheckpointChanged?.();
      setReload(previous => previous + 1);
      await onRefreshGameState?.();
    } catch (err) {
      if (!mounted.current || currentGame.current !== gameId) return;
      setError(err instanceof Error ? err.message : 'Failed to grant');
      if (err instanceof AdminGrantError && err.status === 409) {
        setOptions(null);
        setReload(previous => previous + 1);
      }
    } finally {
      if (mounted.current && currentGame.current === gameId) setSubmitting(false);
    }
  };

  return <div className="debug-items">
    <h4>Grant Items and Resources</h4>
    {loading && <div>Loading grant options…</div>}
    {error && <div className="error" role="alert">{error}</div>}
    {success && <div role="status">{success}</div>}
    <div className="debug-grant-form">
      <label>Investigator:
        <select value={investigator} onChange={event => setInvestigator(event.target.value)} disabled={loading || submitting}>
          {!investigator && <option value="">No investigator available</option>}
          {options?.investigators.map(name => <option key={name} value={name}>{name}</option>)}
        </select>
      </label>
      <label>Type:
        <select value={type} onChange={event => setType(event.target.value as AdminGrantType)} disabled={loading || submitting}>
          {[...itemTypes, 'Money' as const, 'Clues' as const].map(grantType => <option key={grantType} value={grantType}>{labels[grantType]}</option>)}
        </select>
      </label>
      {isResource ? <label>Amount:
        <input type="number" min={1} max={2147483647} step={1} value={amount} onChange={event => setAmount(event.target.value)} disabled={loading || submitting} />
      </label> : <label>Item:
        <select value={deckIndex} onChange={event => setDeckIndex(event.target.value)} disabled={loading || submitting || !entries.length}>
          {!entries.length && <option value="">No items available</option>}
          {entries.map(entry => <option key={entry.deckIndex} value={entry.deckIndex}>{entry.label}</option>)}
        </select>
      </label>}
      <button onClick={grant} disabled={loading || submitting || !canGrant}>{submitting ? 'Granting…' : `Grant to ${investigator || 'investigator'}`}</button>
      <button onClick={() => { setError(null); setReload(previous => previous + 1); }} disabled={loading || submitting}>Reload Options</button>
    </div>
    <details className="debug-loaded-decks"><summary>Loaded decks</summary>
    <div className="debug-items-list">
      {itemTypes.map(itemType => <div key={itemType} className="debug-items-column">
        <div className="debug-items-heading">{labels[itemType]} ({options?.items[itemType].length ?? 0})</div>
        <div className="debug-items-grid">
          {options?.items[itemType].map(entry => <span key={entry.deckIndex} className="debug-item-chip">{entry.label}</span>)}
        </div>
      </div>)}
    </div>
    </details>
  </div>;
};
